import React, { useEffect, useRef, useState } from 'react'
import { Box, ClickAwayListener, Grow, Paper, Popper, Typography } from '@mui/material'

import { ChevronDownIcon } from '~assets/icons'
import { lightTheme } from '~createTheme'

interface ItemSelectorProps {
  onSelect: (type: string) => void
  items: Array<{
    value: string
    label: string
    startItem: React.ReactNode
  }>
  defaultValue?: string
}

interface TypeSelectorItemProps {
  label: string
  startElement: React.ReactNode
  onClick: () => void
}

const TypeSelectorItem: React.FC<TypeSelectorItemProps> = ({ label, startElement, onClick }) => {
  return (
    <Box
      className='flex-v-align'
      gap={1}
      onClick={onClick}
      sx={{
        // @ts-ignore
        '&:hover': { backgroundColor: lightTheme.palette.primary.states.hover },
        p: 1,
        cursor: 'pointer',
        borderRadius: '5px',
      }}
    >
      {startElement}
      <Typography variant='body1'>{label}</Typography>
    </Box>
  )
}

const ItemSelector: React.FC<ItemSelectorProps> = ({ onSelect, items, defaultValue }) => {
  const anchorRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = React.useState<boolean>(false)
  const [selectedItem, setSelectedItem] = useState<(typeof items)[0] | null>(
    () => items.find((item) => item.value === defaultValue) || null,
  )

  const handleSelect = (item: (typeof items)[0]): void => {
    setSelectedItem(item)
    setOpen(false)
    onSelect(item.value)
  }

  useEffect(() => {
    if (defaultValue) {
      setSelectedItem(items.find((item) => item.value === defaultValue) || null)
    }
  }, [defaultValue])

  return (
    <Box display='flex'>
      <Box
        ref={anchorRef}
        className='flex-v-align'
        gap={1}
        sx={{
          cursor: 'pointer',
          '&:hover': { backgroundColor: lightTheme.palette.action.hover },
          p: 1,
          borderRadius: '5px',
        }}
        onClick={() => setOpen(!open)}
      >
        {selectedItem && selectedItem.startItem}
        <Typography variant='body1'>
          {selectedItem ? selectedItem.label : 'Select an item'}
        </Typography>
        <Box
          display='flex'
          sx={{
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s',
          }}
        >
          <ChevronDownIcon />
        </Box>
      </Box>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement='bottom-end'
        transition
        disablePortal
        sx={{ zIndex: 3 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'top right' }}>
            <Paper elevation={8}>
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <Box sx={{ p: 1 }}>
                  {items.map((item) => (
                    <TypeSelectorItem
                      key={item.value}
                      label={item.label}
                      startElement={item.startItem}
                      onClick={() => handleSelect(item)}
                    />
                  ))}
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  )
}

export default ItemSelector
